/* eslint-disable react/react-in-jsx-scope */
/* eslint-disable max-len */

import PageBanner from '../../components/utils/PageBanner';
import { config } from '../../config/awsExports';
import {
  ArrowUpRightIcon,
  CubeIcon,
  CpuChipIcon,
  CodeBracketIcon,
  ComputerDesktopIcon,
  ShareIcon,
  CloudIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import useAnalytics from '../../hooks/useAnalytics';
import {
  URL_RAISE_INCIDENT,
} from '../../utils/constants';

// eslint-disable-next-line import/no-dynamic-require
const { env } = config;

export default function Support() {
  const cardBaseStyle = "bg-white rounded-lg border border-gray-300 p-4 pt-6 mt-5 mx-2 flex flex-col items-center text-center shadow-md"; // Adjusted padding
  const cardSizeStyle = "w-full sm:w-56 h-auto min-h-[230px] flex flex-col justify-between"; // Adjusted for content, responsive width
  const iconStyle = "mb-4 h-8 w-8 text-blue-600";
  const titleStyle = "text-sm font-semibold text-blue-600 mb-2 text-center"; // Adjusted font size
  const titleLongStyle = "text-sm font-semibold text-blue-600 mb-1 text-center"; // Adjusted for potentially longer titles
  const subtitleStyle = "text-xs text-gray-600 mb-3 text-center px-1 flex-grow"; // Adjusted font size, added flex-grow
  const linkStyle = "text-blue-600 hover:text-blue-700 hover:underline text-sm font-semibold";
  const openIconStyle = "ml-1 h-4 w-4";
  const { setAnalytics } = useAnalytics();
  const supportCards = [
    {
      id: "request-review",
      Icon: CubeIcon, // Placeholder for NesspresoIoTIcon
      title: "Request for review – Nespresso IoT B2B",
      subtitle: "Request to review an issue with Nespresso IoT B2B (Skyline, etc).",
      href: env.nesspresoB2BUrl,
      buttonId: "btnOpenSupportRequestReview",
      analyticsName: "review_issue_nn_iot_b2b",
    },
    {
      id: "new-iot-asset",
      Icon: CpuChipIcon, // Placeholder for FirmwareIcon
      title: "New IoT Asset version Analysis and Asset Deploy",
      subtitle: "Request a new asset version to be analyzed for future deployment.",
      href: env.firmwareUrl,
      buttonId: "btnOpenSupportNewIoTAsset",
      analyticsName: "new_iot_asset_version",
    },
    {
      id: "api-credentials",
      Icon: CodeBracketIcon,
      title: "API Credentials",
      subtitle: "Request API credentials.",
      href: env.apiUrl,
      buttonId: "btnOpenSupportAPICredentials",
      analyticsName: "api_credentials",
    },
    {
      id: "iot-device",
      Icon: ComputerDesktopIcon, // Placeholder for IoTDeviceEnviromentIcon
      title: "New IoT Device Environment",
      subtitle: "Request a Staging environment for a new IoT Device including Telemetry Sandbox, FOTA, and Remote Functions.",
      href: env.deviceEnviromentUrl,
      buttonId: "btnOpenSupportNewIoTDeviceEnvironment",
      analyticsName: "new_iot_device_environment",
    },
    {
      id: "data-sharing",
      Icon: ShareIcon, // Placeholder for DataSharingIcon
      title: "IoT Data Sharing",
      subtitle: "Request Data Sharing use case creation/modification.",
      href: env.dataSaringUrl,
      buttonId: "btnOpenSupportIoTDataSharing",
      analyticsName: "iot_data_sharing",
    },
  ];

  const accessCards = [
    {
      id: "request-access",
      Icon: CloudIcon, // Placeholder for IoTPlatformIcon
      title: "Consumer IoT Platform Portal",
      subtitle: "Request access to Consumer IoT Platform Portal.",
      href: env.costumerPlatformUrl,
      buttonId: "btnOpenSupportConsumerIoTPlatformPortal",
      analyticsName: "consumer_iot_portal",
    },
    {
      id: "user-request",
      Icon: UserGroupIcon, // Placeholder for IotNesspresoPortalIcon
      title: "IoT Nespresso Portal",
      subtitle: "IoT Nespresso Portal - User create/modify request.",
      href: env.nesspresoPortalUrl,
      buttonId: "btnOpenSupportIoTNespressoPortal",
      analyticsName: "iot_nespresso_portal",
    },
    {
      id: "access-nespresso-cloud2",
      Icon: CodeBracketIcon, // Re-using ApiIcon concept
      title: "Nestle Professional Cloud2Cloud API Integration",
      subtitle: "Request access to Nespresso Cloud 2 Cloud API.",
      href: env.apiCloud2CloudUrl,
      buttonId: "btnOpenSupportNestleProfessional",
      analyticsName: "nestle_professional_c2c_api",
    },
  ];

  const Card = ({ cardData }) => {
    const { id, Icon, title, subtitle, href, buttonId, analyticsName } = cardData;
    const isLongTitle = title.length > 35; // Heuristic for long titles

    // Determinar el evento según el tipo de card
    const isSupport = supportCards.some(card => card.id === id);
    const analyticsEvent = isSupport ? 'service_requested' : 'access_requested';

    return (
      <div id={id} className={`${cardBaseStyle} ${cardSizeStyle}`}>
        <div>
          <Icon className={iconStyle} aria-hidden="true" />
          <h3 className={isLongTitle ? titleLongStyle : titleStyle}>{title}</h3>
          <p className={subtitleStyle}>{subtitle}</p>
        </div>
        <a
          id={buttonId}
          target="_blank"
          rel="noopener noreferrer"
          href={href}
          className={`${linkStyle} inline-flex items-center mt-auto pb-2`} // Ensure link is at the bottom
          onClick={() => {
            setAnalytics({
              event: analyticsEvent,
              extraParams: { name: analyticsName }
            });
          }}
        >
          OPEN
          <ArrowUpRightIcon className={openIconStyle} />
        </a>
      </div>
    );
  };

  return (
    <div className="h-full w-full scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
      <PageBanner title="SERVICE REQUEST" />
      
      <div className="bg-blue-50 border-b-2 border-blue-200 w-full flex items-center justify-center px-4 py-5">
        <p className="text-lg font-semibold text-gray-700">
          Having problems with Consumer IoT Platform or Portal?
          <a
            id="btnSupportForm"
            target="_blank"
            rel="noopener noreferrer"
            href={URL_RAISE_INCIDENT}
            className="ml-2 text-blue-600 hover:text-blue-700 underline font-bold"
            onClick={() => {
              setAnalytics({
                event: 'button_clicked',
                extraParams: { name: 'service_request-raise_incident' }
              });
            }}
          >
            Raise an incident
          </a>
        </p>
      </div>

      <div className="flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-2 gap-y-5 py-6 w-full max-w-5xl">
          {supportCards.map((card) => (
            <Card key={card.id} cardData={card} />
          ))}
        </div>
      </div>

      <div className="pt-6 sm:pt-8"> {/* Replaces spaceBannerAccess */}
        <PageBanner title="ACCESS REQUEST" />
      </div>

      <div className="flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-2 gap-y-5 py-6 w-full max-w-5xl">
          {accessCards.map((card) => (
            <Card key={card.id} cardData={card} />
          ))}
        </div>
      </div>
    </div>
  );
}
